import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Subject,
  EMPTY,
  of,
} from 'rxjs';
import {
  catchError,
  finalize,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { AiService } from '../../../service/admin/ai.service';
import {
  ChiefComplaintAssistApiResponse,
  ChiefComplaintAssistResponse,
} from '../../../models/chief-complaint-assist.model';
import { formatPatientAgeDisplay } from '../../../shared/patient-age.util';

@Component({
  selector: 'app-chief-complaint-assistant',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './chief-complaint-assistant.component.html',
  styleUrl: './chief-complaint-assistant.component.scss',
})
export class ChiefComplaintAssistantComponent implements OnInit, OnDestroy {
  /** Characters added in one update that indicate load/paste, not typing. */
  private readonly bulkLoadCharDelta = 10;

  @Input()
  set patientId(value: number | null) {
    if (this._patientId === value) {
      return;
    }
    this._patientId = value;
    this.resetAnalysisState();
  }

  get patientId(): number | null {
    return this._patientId;
  }

  @Input() clinicId: number | null = null;
  @Input() patientContext: Record<string, unknown> | null = null;

  @Output() addDiagnosis = new EventEmitter<string>();
  @Output() addNote = new EventEmitter<string>();
  @Output() addReport = new EventEmitter<string>();

  readonly minCharacters = 20;

  loading = false;
  suggestions: ChiefComplaintAssistResponse | null = null;
  errorMessage: string | null = null;
  showPanel = false;
  /** When false, the entire copilot UI is hidden. */
  aiEnabled = true;

  openTooltip: string | null = null;
  missingExpanded = false;
  questionsExpanded = false;

  addedDiagnoses = new Set<string>();
  addedReports = new Set<string>();
  addedNotes = new Set<string>();

  private _symptoms = '';
  private _patientId: number | null = null;
  /** Text that was last sent to the API — used to skip duplicate calls and show stale state. */
  private lastAnalyzedText = '';
  private readonly analyzeRequest$ = new Subject<{ force: boolean }>();
  private readonly destroy$ = new Subject<void>();
  private pendingAutoAnalyze = false;

  constructor(private readonly aiService: AiService) {}

  @Input()
  set symptoms(value: string) {
    const previous = this._symptoms;
    const next = value ?? '';
    this._symptoms = next;

    if (!next.trim()) {
      this.resetAnalysisState();
      return;
    }

    if (this.isBulkSymptomsUpdate(previous, next)) {
      this.scheduleAutoAnalyze();
    }
  }

  get symptoms(): string {
    return this._symptoms;
  }

  get canAnalyze(): boolean {
    return this.symptoms.trim().length >= this.minCharacters;
  }

  /** True when chief complaints changed after the last successful analysis. */
  get isStale(): boolean {
    if (!this.suggestions || !this.lastAnalyzedText) {
      return false;
    }
    return this.symptoms.trim() !== this.lastAnalyzedText;
  }

  ngOnInit(): void {
    this.aiService
      .loadStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.aiEnabled = enabled;
        if (!enabled) {
          this.resetAnalysisState();
        } else if (this.pendingAutoAnalyze) {
          this.pendingAutoAnalyze = false;
          this.scheduleAutoAnalyze();
        }
      });

    this.analyzeRequest$
      .pipe(
        switchMap(({ force }) => {
          if (!this.aiEnabled) {
            return EMPTY;
          }

          const trimmed = this.symptoms.trim();

          if (trimmed.length < this.minCharacters) {
            this.resetAnalysisState();
            return EMPTY;
          }

          if (!force && trimmed === this.lastAnalyzedText && this.suggestions) {
            return EMPTY;
          }

          this.loading = true;
          this.errorMessage = null;
          this.showPanel = true;

          return this.aiService.chiefComplaintAssist({
            symptoms: trimmed,
            clinic_id: this.clinicId,
            patient_id: this.patientId,
            patient_context: this.buildPatientContextPayload(),
          }).pipe(
            catchError((error) => {
              this.errorMessage =
                error?.error?.message ||
                error?.message ||
                'Unable to fetch AI suggestions right now.';
              this.suggestions = null;
              return of(null);
            }),
            finalize(() => {
              this.loading = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: ChiefComplaintAssistApiResponse | null) => {
        if (!response?.success || !response.data) {
          return;
        }

        this.lastAnalyzedText = this.symptoms.trim();
        this.suggestions = response.data;
        if (this.suggestions?.differential_diagnoses) {
          this.suggestions.differential_diagnoses.sort((a, b) => {
            const pA = a.probability_percentage || 0;
            const pB = b.probability_percentage || 0;
            return pB - pA;
          });
        }
        this.errorMessage = null;
        this.showPanel = true;
        this.addedDiagnoses.clear();
        this.addedReports.clear();
        this.addedNotes.clear();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Auto-run when the doctor leaves the chief complaints field after editing. */
  onSymptomsBlur(): void {
    this.autoAnalyzeIfEligible();
  }

  /** Manual run / refresh — still available as a fallback. */
  analyze(force = false): void {
    if (!this.aiEnabled || !this.canAnalyze || this.loading) {
      return;
    }
    this.analyzeRequest$.next({ force });
  }

  onAddDiagnosis(name: string): void {
    if (this.addedDiagnoses.has(name)) return;
    this.addedDiagnoses.add(name);
    this.addDiagnosis.emit(name);
  }

  onAddNote(question: string): void {
    if (this.addedNotes.has(question)) return;
    this.addedNotes.add(question);
    this.addNote.emit(question);
  }

  onAddReport(name: string): void {
    if (this.addedReports.has(name)) return;
    this.addedReports.add(name);
    this.addReport.emit(name);
  }

  toggleTooltip(name: string): void {
    this.openTooltip = this.openTooltip === name ? null : name;
  }

  getConfidenceClass(confidence: string): string {
    const n = (confidence ?? '').trim().toLowerCase();
    if (n === 'most likely') return 'cca-badge cca-badge--most-likely';
    if (n === 'possible')    return 'cca-badge cca-badge--possible';
    if (n === 'consider')    return 'cca-badge cca-badge--consider';
    return 'cca-badge cca-badge--consider';
  }

  get completenessScore(): number {
    if (!this.suggestions) return 0;
    const score = this.suggestions.completeness_score ?? 0;
    return Math.min(100, Math.max(0, score));
  }

  get completenessLabel(): string {
    const s = this.completenessScore;
    if (s < 35) return 'Limited';
    if (s < 65) return 'Partial';
    if (s < 85) return 'Good';
    return 'Complete';
  }

  get completenessClass(): string {
    const s = this.completenessScore;
    if (s < 35) return 'cca-completeness__bar--limited';
    if (s < 65) return 'cca-completeness__bar--partial';
    if (s < 85) return 'cca-completeness__bar--good';
    return 'cca-completeness__bar--complete';
  }

  get hasDiagnoses(): boolean {
    return (
      this.suggestions?.status === 'analysis_available' &&
      (this.suggestions?.differential_diagnoses?.length ?? 0) > 0
    );
  }

  get hasRedFlags(): boolean {
    return (
      this.suggestions?.status === 'analysis_available' &&
      (this.suggestions?.red_flags?.length ?? 0) > 0
    );
  }

  get topDiagnoses() {
    return (this.suggestions?.differential_diagnoses ?? []).slice(0, 3);
  }

  private resetAnalysisState(): void {
    this.loading = false;
    this.suggestions = null;
    this.errorMessage = null;
    this.showPanel = false;
    this.lastAnalyzedText = '';
    this.pendingAutoAnalyze = false;
  }

  /** Prefilled load (assistant doctor) or large paste — not character-by-character typing. */
  private isBulkSymptomsUpdate(previous: string, next: string): boolean {
    const prevTrim = previous.trim();
    const nextTrim = next.trim();

    if (nextTrim.length < this.minCharacters) {
      return false;
    }

    if (prevTrim.length === 0) {
      return true;
    }

    if (nextTrim.length - prevTrim.length >= this.bulkLoadCharDelta) {
      return true;
    }

    return false;
  }

  private scheduleAutoAnalyze(): void {
    if (!this.aiEnabled) {
      this.pendingAutoAnalyze = true;
      return;
    }
    if (this.pendingAutoAnalyze) {
      return;
    }
    this.pendingAutoAnalyze = true;
    queueMicrotask(() => {
      this.pendingAutoAnalyze = false;
      this.autoAnalyzeIfEligible();
    });
  }

  private autoAnalyzeIfEligible(): void {
    if (!this.aiEnabled || !this.canAnalyze || this.loading) {
      return;
    }

    const trimmed = this.symptoms.trim();
    if (trimmed === this.lastAnalyzedText && this.suggestions) {
      return;
    }

    this.analyze(false);
  }

  private buildPatientContextPayload(): Record<string, string> | null {
    if (!this.patientContext) return null;

    const payload: Record<string, string> = {};
    for (const [key, value] of Object.entries(this.patientContext)) {
      if (value === null || value === undefined || value === '') continue;
      if (key === 'age') {
        const normalizedAge = formatPatientAgeDisplay(value as string | number);
        if (normalizedAge !== null && normalizedAge !== '') {
          payload[key] = normalizedAge;
        }
        continue;
      }
      payload[key] = String(value);
    }
    return Object.keys(payload).length > 0 ? payload : null;
  }
}
